import React from "react";
import { createPortal } from "react-dom";

import ListItemButton from "@vapor/react-material/ListItemButton";
import ListItemIcon from "@vapor/react-material/ListItemIcon";
import ListItemText from "@vapor/react-material/ListItemText";
import List from "@vapor/react-material/List";
import Tooltip from "@vapor/react-material/Tooltip";
import { useTheme } from "@vapor/react-material/styles";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";

import { SubLevelMenu } from "./SubLevelMenu";

export const FirstLevelListButtons = ({
  isLarge,
  setSublevel,
  setSublevelSelectedIndex,
  selectedIndex,
  handlePopoverClick,
  handlePopoverClose,
  setSelectedIndex,
  drawerOpen,
  menuLevels,
  sublevel,
  sublevelMenuRef,
  sublevelSelectedIndex,
  openPopover,
  showUpperLevel,
  slideRef
}) => {
  const listItemRefs = React.useRef([]);

  const [autofocus, setAutofocus] = React.useState(false);

  const listItemRef = (childIndex) => (el) => {
    listItemRefs.current[childIndex] = el;
  };

  const moveFocusUp = (childIndex) => {
    focusOn(childIndex - 1);
  };

  const moveFocusDown = (childIndex) => {
    focusOn(childIndex + 1);
  };

  const focusOn = (childIndex) => {
    const childToFocus = listItemRefs.current[childIndex];
    childToFocus?.focus();
  };

  const openSubLevel = ({ childIndex }) => {
    listItemRefs.current[childIndex]?.click();
  };

  const handleKeyDown =
    ({ childIndex }) =>
    (e) => {
      if (e.key === "ArrowUp") {
        moveFocusUp(childIndex);
        return;
      }

      if (e.key === "ArrowDown") {
        moveFocusDown(childIndex);
        return;
      }

      if (e.key === "ArrowRight") {
        openSubLevel({ childIndex });
        return;
      }
      if (e.key === "ArrowLeft") {
        setAutofocus(true);
        return;
      }
    };

  return (
    <>
      <List disablePadding role="menu" autoFocus>
        {menuLevels?.children?.map((child, childIndex) => {
          const listButtonProps = {
            child,
            childIndex,
            setSublevel,
            setSublevelSelectedIndex,
            selectedIndex,
            handlePopoverClick,
            handlePopoverClose,
            setSelectedIndex,
            drawerOpen,
            listItemRef: listItemRef(childIndex),
            handleKeyDown: handleKeyDown({ childIndex }),
            sublevel,
            autofocus
          };

          return (
            <React.Fragment key={childIndex}>
              {isLarge && !drawerOpen ? (
                <Tooltip title={child.label} arrow placement="right">
                  {ListButton(listButtonProps)}
                </Tooltip>
              ) : (
                ListButton(listButtonProps)
              )}
            </React.Fragment>
          );
        })}
      </List>
      {sublevelMenuRef?.current &&
        createPortal(
          <>
            <SubLevelMenu
              openPopover={openPopover}
              drawerOpen={drawerOpen}
              handlePopoverClose={handlePopoverClose}
              showUpperLevel={showUpperLevel}
              sublevel={sublevel}
              setSublevel={setSublevel}
              setSublevelSelectedIndex={setSublevelSelectedIndex}
              sublevelSelectedIndex={sublevelSelectedIndex}
              slideRef={slideRef?.current}
              focusOn={focusOn}
            />
          </>,
          sublevelMenuRef?.current
        )}
    </>
  );
};

export const ListButton = ({
  child,
  childIndex,
  setSublevel,
  setSublevelSelectedIndex,
  selectedIndex,
  handlePopoverClick,
  handlePopoverClose,
  setSelectedIndex,
  drawerOpen,
  handleKeyDown,
  listItemRef,
  sublevel,
  autofocus
}) => {
  const theme: any = useTheme();
  return (
    <ListItemButton
      ref={listItemRef}
      selected={selectedIndex === childIndex}
      onClick={(event) => {
        if (child.children) {
          setSublevel((sl) => {
            if (sl?.ancestor === childIndex) {
              return sl;
            } else {
              setSublevelSelectedIndex(null);
              return child;
            }
          });

          handlePopoverClick(event);
        }
        !child.children && handlePopoverClose();
        setSelectedIndex(childIndex);
        child.onClickFunction && child.onClickFunction();
      }}
      onKeyDown={handleKeyDown}
      sx={{
        pl: drawerOpen ? "16px" : "15px",
        height: 48,
        "&.MuiButtonBase-root.MuiListItemButton-root.Mui-selected": {
          border: `1px solid ${theme.palette.primary.primary} !important`
        },
        "&:focus-visible": {
          background: "#5A6872 !important"
        }
      }}
      role="menuitem"
      aria-haspopup={child.children ? "true" : "false"}
      aria-label={child?.label}
      autoFocus={autofocus}
    >
      <ListItemIcon variant="menuIcon">
        {typeof child.icon === "string" && child.icon.startsWith("fa-") ? (
          <FontAwesomeIcon
            icon={["far", child.icon.substring(3)]}
            // icon={["far", "user"]}
            style={{ width: 16 }}
          />
        ) : (
          child.icon || null
        )}
      </ListItemIcon>

      {drawerOpen && (
        <ListItemText
          primary={child?.label}
          sx={{
            my: 0,
            opacity: drawerOpen ? 1 : 0
          }}
        />
      )}
      {child?.children && drawerOpen && (
        <FontAwesomeIcon
          style={{ color: "white", width: 7, height: 12 }}
          icon={faAngleRight}
        />
      )}
    </ListItemButton>
  );
};
